/**
 * This helper function returns the appropriate text color based on the background color.
 * It implements the WCAG 2.1 relative luminance algorithm.
 * @see https://w3c.github.io/wcag/guidelines/22/#dfn-relative-luminance
 */

/**
 * Converts a hex color channel value to its linear RGB value for luminance calculation.
 * Applies the sRGB inverse comparing function per WCAG 2.1.
 * @param channel - The sRGB channel value (0–255), normalized to 0–1 internally.
 * @returns The linearized channel value.
 */
function linearize(channel: number): number {
  const srgb: number = channel / 255;
  return srgb <= 0.04045 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
}

/**
 * Parses a hex color string into its RGB components.
 * Supports 3-digit (#RGB), 4-digit (#RGBA), 6-digit (#RRGGBB), and 8-digit (#RRGGBBAA) formats.
 * @param hex - The hex color string (with or without leading #).
 * @returns An object with r, g, b values (0–255).
 */
function parseHex(hex: string): { r: number; g: number; b: number } {
  const cleaned: string = hex.replace(/^#/, '');

  let r: number;
  let g: number;
  let b: number;

  if (cleaned.length === 3 || cleaned.length === 4) {
    r = parseInt(cleaned[0] + cleaned[0], 16);
    g = parseInt(cleaned[1] + cleaned[1], 16);
    b = parseInt(cleaned[2] + cleaned[2], 16);
  } else {
    r = parseInt(cleaned.substring(0, 2), 16);
    g = parseInt(cleaned.substring(2, 4), 16);
    b = parseInt(cleaned.substring(4, 6), 16);
  }

  return { r, g, b };
}

/**
 * Calculates the WCAG 2.1 relative luminance of a hex color.
 * @param hex - A hex color string.
 * @returns The relative luminance (0 = darkest black, 1 = lightest white).
 */
export function getRelativeLuminance(hex: string): number {
  const { r, g, b } = parseHex(hex);
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/**
 * Returns black or white text color based on which provides better contrast
 * against the given background color, per WCAG 2.1 relative luminance.
 * @param bgHex - The background hex color string.
 * @returns '#ffffff' for dark backgrounds, '#000000' for light backgrounds.
 */
export function getTextColorForBackground(bgHex: string): string {
  if (!bgHex) {
    return '#000000';
  }
  const luminance: number = getRelativeLuminance(bgHex);

  /**
   * Use white when the background luminance is below the crossover point
   * 0.179 is the luminance at which white and black text yield equal WCAG contrast ratios.
   * Derived from: (1.05)/(L+0.05) = (L+0.05)/(0.05) --> L = 0.179
   * @see https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
   */
  return luminance < 0.179 ? '#ffffff' : '#000000';
}
